<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { breakpointsTailwind, invoke } from '@vueuse/core'
import { getSavedPrompts } from '~~/src/api/users'
import type { User } from '~~/src/types/user'

interface IProps {
  user: User
  lazyLoad?: boolean
  limitHeight?: boolean
  listStyle?: CSSProperties
}

const props = defineProps<IProps>()
const { user, lazyLoad } = toRefs(props)

const { colors } = useTheme()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const pageSize = computed(() => lazyLoad.value ? 5 : 1000)
const pageNumber = ref(1)
const canLoadMore = ref(true)
const isLoading = ref(true)

const enterprise = useEnterprise()

const { data: savedPrompts, pending, refresh } = useAPI(() => {
  return getSavedPrompts({
    userId: user.value.id,
    query: {
      size: pageSize.value,
      enterprise_id: enterprise.value,
    },
  })
})
watch(enterprise, () => refresh())
invoke(async () => {
  await until(pending).toBe(false)
  isLoading.value = false

  if ((savedPrompts.value?.length || 0) < pageSize.value)
    canLoadMore.value = false
})

const loadMorePrompts = async () => {
  if (!canLoadMore.value || !user.value)
    return

  isLoading.value = true
  canLoadMore.value = false

  const newPrompts = await getSavedPrompts({
    userId: user.value.id,
    query: {
      size: pageSize.value,
      page: ++pageNumber.value,
      enterprise_id: enterprise.value,
    },
  })

  if (!newPrompts) {
    canLoadMore.value = true
    isLoading.value = false
    return
  }

  savedPrompts.value = [
    ...savedPrompts.value || [],
    ...newPrompts,
  ]
  isLoading.value = false
  canLoadMore.value = true
}

const { firebaseUser } = useFirebaseAuth()
watch(firebaseUser, () => refresh())

const savedPromptsUpdate = useUpdatePromptSave()
watch(savedPromptsUpdate, () => {
  if (!savedPromptsUpdate.value)
    return

  if (!savedPrompts.value) {
    refresh()
    return
  }

  if (savedPromptsUpdate.value.state === 'removed')
    savedPrompts.value = savedPrompts.value.filter(prompt => prompt.id !== savedPromptsUpdate.value?.id)
  else
    refresh()
})

const savedPromptsWithType = computed(() => savedPrompts.value?.map(prompt => ({ ...prompt, isPrompt: true })))
</script>

<template>
  <div h-full w-full :class="{ 'px-[10px]': isMobile }">
    <PromptList
      v-if="savedPrompts"
      :elementals="savedPromptsWithType"
      :loading="isLoading"
      :variant="isMobile ? 'default' : 'small'"
      :scroll="limitHeight"
      :style="listStyle"
      :show-featured-tag="false"
      :is-searching="true"
      @reached-end="loadMorePrompts"
    >
      <template #not-found>
        You don't have any saved prompts
      </template>
    </PromptList>

    <div v-else center>
      <Text
        v-if="isLoading"
        variant="small"
        :color="colors.bluishDark"
        :class="{
          'mt-5': isMobile,
        }"
      >
        Loading...
      </Text>

      <Text v-else>
        Failed to load prompts
      </Text>
    </div>
  </div>
</template>
